export interface SetTypeTemplate {
  lead: string;
  closer: string;
}

export const SET_TYPE_TEMPLATES: Record<string, SetTypeTemplate> = {
  expansion: {
    lead: 'an expansion set',
    closer: 'introducing new mechanics and cards into Standard play.',
  },
  core: {
    lead: 'a core set',
    closer: 'designed as an entry point with reprints and evergreen mechanics.',
  },
  masters: {
    lead: 'a Masters set',
    closer: 'reprinting valuable cards from earlier sets to make them more accessible.',
  },
  commander: {
    lead: 'a Commander set',
    closer: 'designed for the multiplayer Commander format with new legendary creatures and tools.',
  },
  draft_innovation: {
    lead: 'a draft innovation set',
    closer: 'experimenting with new draft formats and mechanics.',
  },
  funny: {
    lead: 'a non-tournament-legal Un-set',
    closer: 'featuring silver-bordered cards with humorous and experimental designs.',
  },
  promo: {
    lead: 'a promotional release',
    closer: 'distributed through events, stores, and special programs.',
  },
  token: {
    lead: 'a token-only set',
    closer: 'collecting the tokens produced by cards in adjacent expansions.',
  },
  starter: {
    lead: 'a starter product',
    closer: 'designed to introduce new players to Magic.',
  },
  duel_deck: {
    lead: 'a Duel Deck product',
    closer: 'pairing two pre-constructed decks for head-to-head play.',
  },
  from_the_vault: {
    lead: 'a From the Vault collector release',
    closer: 'curating fan-favorite cards in premium foil treatments.',
  },
  spellbook: {
    lead: 'a Signature Spellbook collector release',
    closer: 'pairing iconic cards around a single planeswalker theme.',
  },
  premium_deck: {
    lead: 'a Premium Deck product',
    closer: 'offering a fully foil pre-constructed deck.',
  },
  masterpiece: {
    lead: 'a Masterpiece series',
    closer: 'showcasing premium reprints with bespoke art treatments.',
  },
  arsenal: {
    lead: 'a Universes Beyond release',
    closer: 'crossing Magic with another franchise.',
  },
  box: {
    lead: 'a box set',
    closer: 'released as a standalone product outside the standard release cadence.',
  },
  archenemy: {
    lead: 'an Archenemy product',
    closer: 'designed for the multiplayer Archenemy format with oversized scheme cards.',
  },
  planechase: {
    lead: 'a Planechase product',
    closer: 'designed for the Planechase format with oversized plane cards.',
  },
  vanguard: {
    lead: 'a Vanguard product',
    closer: 'featuring oversized cards that modify the rules of a game.',
  },
  treasure_chest: {
    lead: 'a Treasure Chest release',
    closer: 'distributed through Magic Online Treasure Chest rewards.',
  },
  memorabilia: {
    lead: 'a memorabilia release',
    closer: 'celebrating Magic history with collectible printings.',
  },
  minigame: {
    lead: 'a minigame release',
    closer: 'featuring cards designed for an alternate game format.',
  },
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return 'an unknown release date';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return 'an unknown release date';
  }
};

interface BuildDescriptionInput {
  name: string;
  code: string;
  releasedAt: string | null;
  setType: string | null;
  cardCount: number;
}

export function buildSetDescription({
  name,
  code,
  releasedAt,
  setType,
  cardCount,
}: BuildDescriptionInput): string {
  const codeUpper = code.toUpperCase();
  const dateStr = formatDate(releasedAt);
  const countStr = cardCount.toLocaleString();

  const template = setType ? SET_TYPE_TEMPLATES[setType] : undefined;

  if (template) {
    const releasePhrase =
      releasedAt
        ? `released on ${dateStr}`
        : 'with no listed release date';
    return (
      `${name} (${codeUpper}) is ${template.lead}, ${releasePhrase}. ` +
      `The set contains ${countStr} cards, ${template.closer} ` +
      `Browse the full card list below or trade ${name} cards on TradeMagic.`
    );
  }

  const releaseClause = releasedAt ? ` released on ${dateStr}` : '';
  return (
    `${name} (${codeUpper}) is a Magic: The Gathering set${releaseClause}, containing ${countStr} cards. ` +
    `Browse the full card list below or trade ${name} cards on TradeMagic.`
  );
}
